import { Suspense, type ReactNode } from "react";
import { Navigate, Outlet } from "react-router";
import type { RouteObject } from "react-router";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { QueryClientProvider } from "@tanstack/react-query";
import { TranslationProvider } from "@privacy/translations";
import { ThemeProvider } from "@privacy/ui";
import { queryClient } from "../api/queryClient";
import { PageErrorBoundary } from "../root/error/ErrorPage";
import { TrustPageHeader } from "./TrustHeader";
import { trustGridPageRoutes } from "./TrustGridPage";
import { TrustUserInfoProvider, useTrustUserInfo } from "./TrustUserInfoProvider";
import { TrustUserInfoPayloadSchema } from "./trustAPI";
import { TrustUserInfoDialog } from "./TrustUserInfoDialog";
import { TrustLoginPage } from "./TrustLoginPage";
import { AuthErrorSchema } from "../auth/AuthProvider";
import { TRUST_PATHS } from "./paths";
import { TRUST_LOGIN_HINT_KEY, setTrustLoginHint } from "./trustLoginHint";
import { useLocalStorage } from "../shared/useLocalStorage";
import { getCsrfToken } from "./trustAuthAPI";

const HOME_URL = "/trust";

const TrustBaseProviders = () => {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <TranslationProvider>
          <PageErrorBoundary
            authConfig={{ isAuthenticated: false }}
            includePlaceholderSidebar={false}
            homeUrl={HOME_URL}
          >
            <Suspense fallback={<TrustPageHeader.Skeleton />}>
              <Outlet />
            </Suspense>
            <ReactQueryDevtools buttonPosition="bottom-left" />
          </PageErrorBoundary>
        </TranslationProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

const TrustAuthenticatedLayout = () => {
  const returnUrlHandler = TrustLoginPage.useReturnUrl(TRUST_PATHS.grid());
  const [isLoggedIn] = useLocalStorage(TRUST_LOGIN_HINT_KEY, (v): v is boolean => typeof v === "boolean", false);

  if (!isLoggedIn) {
    return <Navigate to={`${TRUST_PATHS.login()}?${returnUrlHandler.constructSearchParams()}`} replace />;
  }

  return (
    <PageErrorBoundary
      authConfig={{
        isAuthenticated: true,
        errorHandler: {
          handler: () => {
            setTrustLoginHint(false);
            location.replace(`${TRUST_PATHS.login()}?${returnUrlHandler.constructSearchParams()}`);
          },
          isAuthError: error => AuthErrorSchema.safeParse(error).success,
        },
      }}
      homeUrl={HOME_URL}
      includePlaceholderSidebar={false}
    >
      <TrustUserInfoProvider>
        <TrustPageHeader />
        <Trust>
          <Outlet />
        </Trust>
      </TrustUserInfoProvider>
    </PageErrorBoundary>
  );
};

const Trust = ({ children }: { children: ReactNode }) => {
  const userInfo = useTrustUserInfo();

  if (!TrustUserInfoPayloadSchema.safeParse(userInfo).success) return <TrustUserInfoDialog userInfo={userInfo} />;

  return children;
};

export const trustRoute = {
  path: "verify",
  Component: TrustBaseProviders,
  children: [
    { path: "login", Component: TrustLoginPage },
    {
      Component: TrustAuthenticatedLayout,
      loader: () => getCsrfToken(),
      children: trustGridPageRoutes,
    },
  ],
} satisfies RouteObject;
